import { workTypeLabel } from '../utils/workTypes';
import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/Layout';
import UserAvatar, { avatarTraits } from '../components/UserAvatar';
import { STATUS_LABELS, StatusPill, TypeTile, isOverdue, taskTitle } from '../components/TaskBits';
import { formatDate, relativeDay } from '../utils/api';
import { useTasks, useTeamMembers } from '../lib/queries';
import { Button } from '../components/ui/button';
import AppSelect from '../components/ui/app-select';
import DateField from '../components/ui/date-field';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowUpRight, Camera, CheckSquare, Layers, Mail, Scissors, Search, Users as UsersIcon, Video, X } from 'lucide-react';
import { toast } from 'sonner';

const SPECIALIZATIONS = {
  photo: { label: 'Photography', icon: Camera },
  video: { label: 'Videography', icon: Video },
  editing: { label: 'Editing', icon: Scissors },
  other: { label: 'Other', icon: Layers }
};
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const EMPTY_FILTERS = { from: '', to: '', month: '', year: '', date: '' };
const time = (date) => new Date(date || 0).getTime() || 0;

const summarize = (list) => {
  const assigned = list.filter((t) => t.status === 'assigned').length;
  const inProgress = list.filter((t) => t.status === 'in_progress').length;
  const completed = list.filter((t) => t.status === 'completed').length;
  return {
    total: list.length,
    assigned,
    inProgress,
    completed,
    overdue: list.filter(isOverdue).length,
    active: assigned + inProgress
  };
};

const Employees = () => {
  const { data: employees = [], isPending: membersPending, isError, error: loadError } = useTeamMembers();
  const { data: allTasks = [], isPending: tasksPending } = useTasks();
  const loading = membersPending || tasksPending;
  const [search, setSearch] = useState('');
  const [spec, setSpec] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterType, setFilterType] = useState('none');
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load employees', {
        description: loadError?.response?.data?.detail || 'Please try again'
      });
    }
  }, [isError, loadError]);

  const tasksByEmployee = useMemo(() => {
    const map = {};
    allTasks.forEach((t) => {
      if (!t.assigned_to) return;
      (map[t.assigned_to] = map[t.assigned_to] || []).push(t);
    });
    return map;
  }, [allTasks]);

  const roster = useMemo(() => {
    const query = search.trim().toLowerCase();
    return employees
      .filter(
        (e) =>
          (spec === 'all' || e.specialization === spec) &&
          (!query || e.name.toLowerCase().includes(query) || e.email.toLowerCase().includes(query))
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, search, spec]);

  const team = useMemo(() => summarize(allTasks.filter((t) => employees.some((e) => e.id === t.assigned_to))), [allTasks, employees]);

  const openEmployee = (employee) => {
    setSelectedEmployee(employee);
    setStatusFilter('all');
    setFilterType('none');
    setFilters(EMPTY_FILTERS);
  };

  const employeeTasks = useMemo(
    () => (selectedEmployee ? tasksByEmployee[selectedEmployee.id] || [] : []),
    [selectedEmployee, tasksByEmployee]
  );
  const employeeStats = useMemo(() => summarize(employeeTasks), [employeeTasks]);

  const filteredTasks = useMemo(() => {
    const parse = (value) => (value ? new Date(value) : null);
    let data = [...employeeTasks];
    if (filterType === 'range' && filters.from && filters.to) {
      const from = new Date(`${filters.from}T00:00:00`);
      const to = new Date(`${filters.to}T23:59:59`);
      data = data.filter((t) => {
        const created = parse(t.created_at);
        return created && created >= from && created <= to;
      });
    } else if (filterType === 'month' && filters.month && filters.year) {
      data = data.filter((t) => {
        const created = parse(t.created_at);
        return (
          created &&
          created.getMonth() + 1 === Number(filters.month) &&
          created.getFullYear() === Number(filters.year)
        );
      });
    } else if (filterType === 'year' && filters.year) {
      data = data.filter((t) => {
        const created = parse(t.created_at);
        return created && created.getFullYear() === Number(filters.year);
      });
    } else if (filterType === 'date' && filters.date) {
      data = data.filter((t) => (t.created_at ? t.created_at.slice(0, 10) : '') === filters.date);
    }
    if (statusFilter !== 'all') data = data.filter((t) => t.status === statusFilter);
    return data.sort((a, b) => time(b.created_at) - time(a.created_at));
  }, [employeeTasks, filterType, filters, statusFilter]);

  const openTaskLink = (link) => {
    if (link) {
      window.open(link, '_blank', 'noopener');
    } else {
      toast.error('No deliverable link yet', { description: 'This task has not been delivered' });
    }
  };

  const STATUS_TABS = [
    { id: 'all', label: 'All', count: employeeStats.total },
    { id: 'assigned', label: 'Assigned', count: employeeStats.assigned },
    { id: 'in_progress', label: 'In progress', count: employeeStats.inProgress },
    { id: 'completed', label: 'Completed', count: employeeStats.completed }
  ];
  const selectedSpec = SPECIALIZATIONS[selectedEmployee?.specialization];
  const SelectedSpecIcon = selectedSpec?.icon;
  const hasFilters = statusFilter !== 'all' || filterType !== 'none';

  return (
    <Layout>
      <div className="dashboard-page institutions-page">
        <div className="dashboard-heading">
          <div>
            <p className="page-eyebrow">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
            </p>
            <h1>Employees</h1>
            <p>Your team and what each person is working on.</p>
          </div>
        </div>

        {loading ? (
          <div className="dashboard-loading" role="status">
            <div className="brand-spinner" />
            <h2>Loading employees</h2>
            <p>Fetching your team and their workload.</p>
          </div>
        ) : (
          <>
            <div className="summary-strip" aria-label="Team summary">
              <span>
                <UsersIcon size={16} />
                <strong>{employees.length}</strong> Employees
              </span>
              <span>
                <i className="strip-dot strip-blue" />
                <strong>{team.active}</strong> Active tasks
              </span>
              <span>
                <i className="strip-dot" />
                <strong>{team.completed}</strong> Completed
              </span>
              <span>
                <i className="strip-dot strip-rose" />
                <strong>{team.overdue}</strong> Overdue
              </span>
            </div>

            <section className="dashboard-panel inst-panel" aria-labelledby="team-title">
              <div className="panel-heading inst-panel-heading">
                <div className="inst-title-group">
                  <h2 id="team-title">
                    Team members <span className="count-pill">{roster.length}</span>
                  </h2>
                  <AppSelect
                    aria-label="Filter by specialization"
                    value={spec}
                    onChange={(e) => setSpec(e.target.value)}
                    options={[
                      { value: 'all', label: 'Any specialization' },
                      ...Object.entries(SPECIALIZATIONS).map(([value, meta]) => ({ value, label: meta.label }))
                    ]}
                  />
                </div>
                <div className="event-search inst-search">
                  <Search size={18} aria-hidden="true" />
                  <input
                    aria-label="Search employees"
                    type="search"
                    placeholder="Search name or email"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button onClick={() => setSearch('')} aria-label="Clear search">
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {roster.length ? (
                <ul className="people-grid">
                  {roster.map((emp) => {
                    const stats = summarize(tasksByEmployee[emp.id] || []);
                    const empSpec = SPECIALIZATIONS[emp.specialization];
                    const SpecIcon = empSpec?.icon;
                    const pct = (n) => (stats.total ? (n / stats.total) * 100 : 0);
                    // The bar wears the same colors as this person's avatar.
                    const tone = avatarTraits(emp.id);
                    const toneVars = {
                      '--wl-done': tone.from,
                      '--wl-progress': tone.to,
                      '--wl-assigned': tone.rim,
                      '--wl-track': tone.tint
                    };
                    return (
                      <li key={emp.id}>
                        <article
                          className="person-card employee-card"
                          data-testid={`employee-card-${emp.id}`}
                          onClick={() => openEmployee(emp)}
                        >
                          <div className="person-top">
                            <UserAvatar id={emp.id} name={emp.name} size={58} className="person-avatar" />
                            {stats.overdue > 0 && <span className="overdue-tag">{stats.overdue} overdue</span>}
                          </div>
                          <h3 className="person-name">{emp.name}</h3>
                          <p className="person-email">
                            <Mail size={14} />
                            {emp.email}
                          </p>
                          {empSpec && (
                            <div className="person-pills">
                              <span className="event-status spec-pill">
                                <SpecIcon size={12} />
                                {empSpec.label}
                              </span>
                            </div>
                          )}

                          <div className="workload" style={toneVars}>
                            <div className="workload-head">
                              <span>Workload</span>
                              <strong>
                                {stats.total ? `${stats.completed}/${stats.total} done` : 'No tasks'}
                              </strong>
                            </div>
                            <div className="workload-bar" aria-hidden="true">
                              <span className="wl-done" style={{ width: `${pct(stats.completed)}%` }} />
                              <span className="wl-progress" style={{ width: `${pct(stats.inProgress)}%` }} />
                              <span className="wl-assigned" style={{ width: `${pct(stats.assigned)}%` }} />
                            </div>
                            <div className="workload-legend">
                              <span><i className="wl-dot wl-assigned" />{stats.assigned} assigned</span>
                              <span><i className="wl-dot wl-progress" />{stats.inProgress} in progress</span>
                            </div>
                          </div>

                          <div className="person-foot">
                            <span className="employee-cta">
                              View tasks <ArrowUpRight size={14} />
                            </span>
                          </div>
                        </article>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="dashboard-empty">
                  <UsersIcon size={28} />
                  <h3>{search || spec !== 'all' ? 'No matching employees' : 'No employees yet'}</h3>
                  <p>
                    {search || spec !== 'all'
                      ? 'Try a different search or clear your filters.'
                      : 'Team members you add will appear here.'}
                  </p>
                  {(search || spec !== 'all') && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearch('');
                        setSpec('all');
                      }}
                    >
                      Clear filters
                    </Button>
                  )}
                </div>
              )}

              <div className="table-footer">
                <span role="status" aria-live="polite">
                  {roster.length === employees.length
                    ? `${employees.length} employees`
                    : `${roster.length} of ${employees.length} employees`}
                </span>
              </div>
            </section>
          </>
        )}
      </div>

      <Dialog open={!!selectedEmployee} onOpenChange={(open) => !open && setSelectedEmployee(null)}>
        <DialogContent className="sm:max-w-[860px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">Tasks for {selectedEmployee?.name}</DialogTitle>
          </DialogHeader>
          {selectedEmployee && (
            <div className="employee-sheet">
              <div className="employee-sheet-head">
                <UserAvatar id={selectedEmployee.id} name={selectedEmployee.name} size={60} className="person-avatar" />
                <div className="min-w-0">
                  <h3>{selectedEmployee.name}</h3>
                  <p>
                    <Mail size={14} />
                    {selectedEmployee.email}
                  </p>
                </div>
                {selectedSpec && (
                  <span className="event-status spec-pill">
                    <SelectedSpecIcon size={12} />
                    {selectedSpec.label}
                  </span>
                )}
              </div>

              <div className="stat-tiles">
                <div>
                  <span>Assigned</span>
                  <strong>{employeeStats.assigned}</strong>
                </div>
                <div>
                  <span>In progress</span>
                  <strong>{employeeStats.inProgress}</strong>
                </div>
                <div>
                  <span>Completed</span>
                  <strong>{employeeStats.completed}</strong>
                </div>
                <div className={employeeStats.overdue ? 'is-alert' : ''}>
                  <span>Overdue</span>
                  <strong>{employeeStats.overdue}</strong>
                </div>
              </div>

              <div className="events-toolbar-row sheet-toolbar">
                <div className="tab-bar" role="tablist" aria-label="Task status">
                  {STATUS_TABS.map((t) => (
                    <button
                      key={t.id}
                      role="tab"
                      aria-selected={statusFilter === t.id}
                      className={statusFilter === t.id ? 'is-active' : ''}
                      onClick={() => setStatusFilter(t.id)}
                    >
                      {t.label}
                      <span>{t.count}</span>
                    </button>
                  ))}
                </div>
                <div className="toolbar-controls">
                  <AppSelect
                    aria-label="Filter by assigned date"
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      setFilters(EMPTY_FILTERS);
                    }}
                    options={[
                      { value: 'none', label: 'Any assigned date' },
                      { value: 'range', label: 'Date range' },
                      { value: 'month', label: 'Month' },
                      { value: 'year', label: 'Year' },
                      { value: 'date', label: 'Specific date' }
                    ]}
                  />
                  {filterType === 'range' && (
                    <div className="date-range">
                      <DateField
                        aria-label="From"
                        placeholder="From"
                        value={filters.from}
                        max={filters.to || undefined}
                        onChange={(e) => setFilters({ ...filters, from: e.target.value })}
                      />
                      <span>to</span>
                      <DateField
                        aria-label="To"
                        placeholder="To"
                        value={filters.to}
                        min={filters.from || undefined}
                        onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                      />
                    </div>
                  )}
                  {filterType === 'month' && (
                    <>
                      <AppSelect
                        aria-label="Month"
                        placeholder="Month"
                        value={filters.month}
                        onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                        options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))}
                      />
                      <Input
                        type="number"
                        aria-label="Year"
                        placeholder="Year"
                        className="year-input"
                        value={filters.year}
                        onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                      />
                    </>
                  )}
                  {filterType === 'year' && (
                    <Input
                      type="number"
                      aria-label="Year"
                      placeholder="Year"
                      className="year-input"
                      value={filters.year}
                      onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                    />
                  )}
                  {filterType === 'date' && (
                    <div className="date-range">
                      <DateField
                        aria-label="Date"
                        value={filters.date}
                        onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                      />
                    </div>
                  )}
                  {hasFilters && (
                    <button
                      className="footer-refresh"
                      onClick={() => {
                        setStatusFilter('all');
                        setFilterType('none');
                        setFilters(EMPTY_FILTERS);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              {filteredTasks.length ? (
                <ul className="emp-task-list">
                  {filteredTasks.map((task) => {
                    const overdue = isOverdue(task);
                    return (
                      <li key={task.id}>
                        <article
                          className="emp-task"
                          onClick={() => openTaskLink(task.deliverable_link)}
                          title={task.deliverable_link ? 'Open deliverable' : 'No deliverable link yet'}
                        >
                          <TypeTile type={task.type} size={42} />
                          <div className="emp-task-body">
                            <strong>{taskTitle(task)}</strong>
                            <p>
                              {workTypeLabel(task.type)}
                              {task.comments ? ` · ${task.comments}` : ''}
                            </p>
                          </div>
                          <div className="emp-task-dates">
                            <span className={overdue ? 'is-overdue' : ''}>
                              {task.due_date
                                ? `${overdue ? 'Overdue · ' : 'Due '}${relativeDay(task.due_date)}`
                                : 'No due date'}
                            </span>
                            <small>
                              Assigned {task.created_at ? formatDate(task.created_at) : '—'}
                            </small>
                          </div>
                          <StatusPill status={task.status} />
                          {task.deliverable_link && <ArrowUpRight size={16} className="emp-task-link" />}
                        </article>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="dashboard-empty sheet-empty">
                  <CheckSquare size={26} />
                  <h3>{employeeStats.total ? 'No matching tasks' : 'No tasks assigned yet'}</h3>
                  <p>
                    {employeeStats.total
                      ? 'Try a different status or date filter.'
                      : `${selectedEmployee.name} has no tasks right now.`}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-2">
            <Button variant="ghost" onClick={() => setSelectedEmployee(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Employees;
